/* PFH 20 Oct 2026: collection filters without page reloads (Pet Circle / Chewy style).
   - Ticking a box, a price range, Go, a remove-filter tag, Clear all or a page number
     fetches the new results and swaps in the grid, counts and sidebar.
   - Open/closed groups and "See more" stay as they were.
   - Back/forward buttons work (URL is kept in step).
   - Phone: "Filter (2)" shows how many filters are on, a floating "Filter & sort"
     button appears once the top bar scrolls away, and Sort lives inside the panel.
   Any fetch problem falls back to a normal page load, so filtering never breaks. */
(() => {
  const root = document.querySelector('[data-section="collectionTemplate"]');
  if (!root || !root.querySelector('.storefront-filter')) return;
  const mobile = window.matchMedia('(max-width: 767px)');
  let controller;

  const $ = (sel, el = document) => el.querySelector(sel);
  const $$ = (sel, el = document) => Array.from(el.querySelectorAll(sel));

  // ---------- Accordions and See more (page-wide, so they survive a refresh) ----------
  document.addEventListener('click', (event) => {
    const acc = event.target.closest('.storefront-filter .pfh-acc');
    if (acc) {
      const body = acc.parentElement.querySelector('.widget-collapse-hide');
      const open = acc.classList.contains('widget-collapse-show');
      acc.classList.toggle('widget-collapse-show', !open);
      acc.setAttribute('aria-expanded', String(open));
      if (body) window.jQuery ? jQuery(body).stop(true, true)[open ? 'slideDown' : 'slideUp'](200) : (body.style.display = open ? '' : 'none');
      return;
    }
    const more = event.target.closest('.storefront-filter .pfh-see-more');
    if (more) {
      const list = more.parentElement.querySelector('.categories-list');
      const open = !list.classList.contains('is-expanded');
      list.classList.toggle('is-expanded', open);
      more.setAttribute('aria-expanded', String(open));
      more.textContent = open ? more.dataset.less : more.dataset.more;
    }
  });

  // ---------- Remember UI state across a refresh ----------
  const groupKey = (group) => (group.querySelector('.pfh-acc')?.textContent || '').trim();
  function saveState() {
    const state = {};
    $$('.storefront-filter .widget-collapse').forEach((g) => {
      state[groupKey(g)] = {
        open: !g.querySelector('.pfh-acc')?.classList.contains('widget-collapse-show'),
        more: !!g.querySelector('.categories-list.is-expanded'),
      };
    });
    return state;
  }
  function restoreState(state) {
    $$('.storefront-filter .widget-collapse').forEach((g) => {
      const s = state[groupKey(g)];
      if (!s) return;
      const acc = g.querySelector('.pfh-acc');
      const body = g.querySelector('.widget-collapse-hide');
      acc.classList.toggle('widget-collapse-show', !s.open);
      acc.setAttribute('aria-expanded', String(s.open));
      if (body) body.style.display = s.open ? '' : 'none';
      const list = g.querySelector('.categories-list');
      const more = g.querySelector('.pfh-see-more');
      if (list && more && s.more) {
        list.classList.add('is-expanded');
        more.setAttribute('aria-expanded', 'true');
        more.textContent = more.dataset.less;
      }
    });
  }

  // ---------- Build a URL from the filter form ----------
  function urlFromForm(form) {
    const url = new URL(window.location.pathname, window.location.origin);
    const current = new URL(window.location.href);
    const params = new URLSearchParams();
    let sort = current.searchParams.get('sort_by');
    for (const [key, value] of new FormData(form)) {
      if (key === 'pfh_sort') { if (mobile.matches) sort = value; continue; }
      if (value === '') continue;
      params.append(key, value);
    }
    if (sort) params.set('sort_by', sort);
    url.search = params.toString();
    return url.toString();
  }

  // Card snippets tag their size / colour strips with inline scripts (grid-color-img-swatch),
  // which don't run when HTML is swapped in. Repeat them so the strips stay hidden.
  function cardClasses() {
    $$('.size_varient', root).forEach((el) => el.parentElement?.classList.add('size_var_parent'));
    $$('.size_var_parent', root).forEach((el) => el.parentElement?.classList.add('size_var_conatiner'));
    $$('.color_img_variant', root).forEach((el) => el.closest('ul')?.classList.add('color_img_parent'));
    $$('.color_img_parent', root).forEach((el) => el.parentElement?.classList.add('color_img_conatiner'));
  }

  // ---------- Fetch and swap ----------
  async function refresh(href, { push = true, scrollToGrid = false } = {}) {
    if (controller) controller.abort();
    controller = new AbortController();
    const grid = $('.shop-bottom-area', root);
    const state = saveState();
    const focusedName = document.activeElement?.name;
    const focusedValue = document.activeElement?.value;
    root.classList.add('pfh-is-loading');
    try {
      const res = await fetch(href, { signal: controller.signal, credentials: 'same-origin' });
      if (!res.ok) throw new Error(res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const next = doc.querySelector('[data-section="collectionTemplate"]');
      if (!next) throw new Error('no section');

      const swap = (sel) => {
        const a = $(sel, root);
        const b = $(sel, next);
        if (a && b) a.replaceWith(b);
      };
      swap('.shop-bottom-area');
      swap('.storefront-filter');
      const countA = $('.select-shoing-wrap > p', root);
      const countB = $('.select-shoing-wrap > p', next);
      if (countA && countB) countA.textContent = countB.textContent;
      swap('.pfh-top-count');
      const showA = $('.pfh-filter-show', root);
      const showB = $('.pfh-filter-show', next);
      if (showA && showB) showA.textContent = showB.textContent;

      cardClasses();
      restoreState(state);
      if (push) history.pushState({ pfhFilters: true }, '', href);
      updateActiveCount();
      if (focusedName) {
        const again = $$(`.storefront-filter [name="${CSS.escape(focusedName)}"]`).find((el) => el.value === focusedValue);
        if (again) again.focus({ preventScroll: true });
      }
      if (scrollToGrid && grid) {
        const top = $('.shop-top-bar', root) || $('.shop-bottom-area', root);
        window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
      }
      document.dispatchEvent(new CustomEvent('pfh:filters-updated'));
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.href = href;
    } finally {
      root.classList.remove('pfh-is-loading');
    }
  }

  // ---------- Events ----------
  let debounce;
  document.addEventListener('change', (event) => {
    const input = event.target.closest('.storefront-filter input[type="checkbox"], .storefront-filter input[name="pfh_sort"]');
    if (!input) return;
    clearTimeout(debounce);
    debounce = setTimeout(() => refresh(urlFromForm(input.form)), 150);
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.storefront-filter form');
    if (!form) return;
    event.preventDefault();
    refresh(urlFromForm(form));
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest(
      '.storefront-filter a.pfh-price-range, .storefront-filter a.active-filter-data-remove, .storefront-filter a.active-filters-clear, .storefront-filter a.filter-reset-selected-value, [data-section="collectionTemplate"] .pro-pagination-style a[href]'
    );
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('javascript')) return;
    event.preventDefault();
    refresh(new URL(href, window.location.href).toString(), { scrollToGrid: !!link.closest('.pro-pagination-style') });
  });
  window.addEventListener('popstate', () => refresh(window.location.href, { push: false }));
  // The phone panel's X sits inside the part that gets swapped, so hand its click to the
  // panel's own close button (which pfh-mobile-filters.js wired once at load).
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.storefront-filter [data-pfh-filter-close]')) return;
    const panel = event.target.closest('[data-pfh-mobile-filters]');
    panel?.querySelector('.pfh-mobile-filter-close')?.click();
  });

  // ---------- Phone: "Filter (2)" and a floating Filter & sort button ----------
  const toggle = $('.pfh-mobile-filter-toggle', root);
  let floating;
  function activeCount() {
    return $$('.storefront-filter .selected-filter-value .active-filter-data-remove').length;
  }
  function updateActiveCount() {
    const n = activeCount();
    if (toggle) toggle.textContent = n ? `Filter (${n})` : 'Filter';
    if (floating) floating.querySelector('span').textContent = n ? `Filter & sort (${n})` : 'Filter & sort';
  }
  if (toggle && 'IntersectionObserver' in window) {
    floating = document.createElement('button');
    floating.type = 'button';
    floating.className = 'pfh-filter-float';
    floating.hidden = true;
    floating.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4"/></svg><span>Filter &amp; sort</span>';
    floating.addEventListener('click', () => toggle.click());
    document.body.append(floating);
    const bar = $('.shop-top-bar', root) || toggle;
    let barGone = false;
    let endVisible = false;
    const sync = () => { floating.hidden = !(mobile.matches && barGone && !endVisible); };
    new IntersectionObserver(([entry]) => {
      barGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sync();
    }).observe(bar);
    // Hide it again near the page numbers / footer so it never covers them.
    const endObserver = new IntersectionObserver(([entry]) => { endVisible = entry.isIntersecting; sync(); });
    const watchEnd = () => {
      endObserver.disconnect();
      endVisible = false;
      const end = $('.pro-pagination-style', root);
      if (end) endObserver.observe(end);
    };
    watchEnd();
    document.addEventListener('pfh:filters-updated', watchEnd);
    mobile.addEventListener('change', sync);
  }
  updateActiveCount();

  // ---------- Line the sidebar up with the page title at every laptop width ----------
  // (the title and the sidebar sit in different sections whose padding differs per width)
  const sidebar = $('.shop-area-sidebar', root);
  const title = document.querySelector('.cd-title h1') || document.querySelector('h1');
  function align() {
    if (!sidebar || !title) return;
    sidebar.style.marginLeft = '';
    if (window.matchMedia('(max-width: 767px)').matches) return;
    const head = $('.pfh-filter-head', sidebar);
    if (!head) return;
    const diff = title.getBoundingClientRect().left - head.getBoundingClientRect().left;
    if (Math.abs(diff) > 0.5 && Math.abs(diff) < 60) {
      sidebar.style.marginLeft = `${parseFloat(getComputedStyle(sidebar).marginLeft) + diff}px`;
    }
  }
  align();
  let resizeTimer;
  window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(align, 150); });
})();
